/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  CForm, CRow, CCol, CFormInput, CFormLabel, CFormTextarea,
  CButton, CSpinner, CAlert
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilSave, cilX } from '@coreui/icons';
import PropTypes from 'prop-types';
import { useTranslation } from '../../hooks/useTranslation';

const CurrencyInputs = ({
  onSubmit, onCancel, isLoading = false,
  submitResponse = null, initialData = null, isEdit = false
}) => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    currencyName: '',
    currencyDescription: ''
  });

  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        currencyName: initialData.CurrencyName || '',
        currencyDescription: initialData.CurrencyDescription || ''
      });
    }
  }, [initialData]);

  useEffect(() => {
    if (submitResponse?.Result?.ErrorCode === 0) {
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
      if (!isEdit) {
        setFormData({ currencyName: '', currencyDescription: '' });
      }
    }
  }, [submitResponse, isEdit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.currencyName.trim()) newErrors.currencyName = t('currencyNameRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const sessionUserId = localStorage.getItem('sessionUserId');
    if (!sessionUserId) {
      setErrors({ general: t('userSessionNotFound') });
      return;
    }

    const payload = {
      SessionUserId: sessionUserId,
      CurrencyName: formData.currencyName.trim(),
      CurrencyDescription: formData.currencyDescription.trim(),
    };

    if (isEdit && initialData?.CurrencyId) {
      payload.CurrencyId = initialData.CurrencyId;
    }

    onSubmit(payload);
  };

  const handleCancel = () => {
    if (!isEdit) setFormData({ currencyName: '', currencyDescription: '' });
    setErrors({});
    onCancel();
  };

  const getSuccessMessage = () => {
    if (!isEdit) {
      return t('currencyCreated');
    } else {
      return t('currencyUpdated');
    }
  };

  return (
    <div>
      {showSuccess && (
        <CAlert color="success" dismissible onClose={() => setShowSuccess(false)}>
          {getSuccessMessage()}
        </CAlert>
      )}

      {errors.general && (
        <CAlert color="danger" dismissible onClose={() => setErrors(prev => ({ ...prev, general: '' }))}>
          {errors.general}
        </CAlert>
      )}

      {submitResponse && submitResponse.Result && submitResponse.Result.ErrorCode !== 0 && submitResponse.Result.ErrorDescription && (
        <CAlert color="danger">
          {submitResponse.Result.ErrorDescription}
        </CAlert>
      )}

      <CForm onSubmit={handleSubmit}>
        <CRow className="mb-3">
          <CCol md={12}>
            <CFormLabel htmlFor="currencyName" style={{ fontStyle: 'normal' }}>
              {t('currencyName')}
            </CFormLabel>
            <CFormInput
              type="text"
              id="currencyName"
              name="currencyName"
              value={formData.currencyName}
              onChange={handleInputChange}
              invalid={!!errors.currencyName}
              disabled={isLoading}
              placeholder={t('enterCurrencyName')}
              style={{ fontStyle: 'normal' }}
            />
            {errors.currencyName && (
              <div className="invalid-feedback d-block" style={{ fontStyle: 'normal' }}>
                {errors.currencyName}
              </div>
            )}
          </CCol>
        </CRow>

        <CRow className="mb-3">
          <CCol md={12}>
            <CFormLabel htmlFor="currencyDescription" style={{ fontStyle: 'normal' }}>
              {t('Description')}
            </CFormLabel>
            <CFormTextarea
              id="currencyDescription"
              name="currencyDescription"
              rows={4}
              value={formData.currencyDescription}
              onChange={handleInputChange}
              invalid={!!errors.currencyDescription}
              disabled={isLoading}
              placeholder={t('enterCurrencyDescription')}
              style={{ fontStyle: 'normal' }}
            />
            {errors.currencyDescription && (
              <div className="invalid-feedback d-block" style={{ fontStyle: 'normal' }}>
                {errors.currencyDescription}
              </div>
            )}
          </CCol>
        </CRow>

        <CRow>
          <CCol className="d-flex gap-2">
            <CButton type="submit" color="primary" disabled={isLoading} style={{ fontStyle: 'normal' }}>
              {isLoading
                ? (<><CSpinner size="sm" className="me-2" /> {t('saving')}</>)
                : (<><CIcon icon={cilSave} className="me-2" /> {t('save')}</>)}
            </CButton>
            <CButton 
              color="secondary" 
              variant="outline" 
              onClick={handleCancel} 
              disabled={isLoading}
              style={{ fontStyle: 'normal' }}
            >
              <CIcon icon={cilX} className="me-2" /> {t('cancel')}
            </CButton>
          </CCol>
        </CRow>
      </CForm>
    </div>
  );
};

CurrencyInputs.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  submitResponse: PropTypes.object,
  initialData: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default CurrencyInputs;